import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, FolderPlus, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, fmtUSD, safe } from "@/lib/api";
import { Band, btnCls, ghostCls, inputCls } from "./FraudShared";

const ACTIONS = ["Allow / release", "Request verification", "Hold", "Block / reject"];

export function FraudDetail({ id, onChange }) {
  const [t, setT] = useState(null);
  useEffect(() => { api.get(`/fraud/transactions/${id}`).then((r) => setT(r.data)); }, [id]);
  if (!t) return <div className="text-sm text-slate-500">Loading…</div>;
  const s = t.scored;
  const upd = (d) => { setT(d); onChange(); };
  return (
    <div className="space-y-4 min-w-0" data-testid="fraud-detail">
      <div className="panel p-5">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <div className="eyebrow">{t.id} · {t.rail} · {t.direction} · {t.stage}</div>
            <div className="text-lg font-semibold text-slate-900 mt-1">{fmtUSD(t.amount_usd)} <span className="text-sm font-normal text-slate-500">{t.customer} {t.direction === "Inbound" ? `from ${t.party}` : `to ${t.beneficiary}`}</span></div>
            {t.note && <div className="text-[11px] text-slate-500 mt-1">{t.note}</div>}
          </div>
          <div className="text-right space-y-1">
            <div className="num text-4xl font-bold text-slate-900" data-testid="fraud-detail-score">{s.score}<span className="text-base text-slate-400">/100</span></div>
            <Band b={s.band} id="fraud-detail-band" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 mt-4 text-xs">
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-slate-500">Recommended</div><div className="font-semibold text-slate-900" data-testid="fraud-detail-recommended">{s.recommended}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-slate-500">Applied ({s.version})</div><div className="font-semibold text-slate-900" data-testid="fraud-detail-applied">{s.applied}</div></div>
          <div className="rounded-lg bg-slate-50 p-3"><div className="text-slate-500">Model fraud likelihood</div><div className="font-semibold text-slate-900" data-testid="fraud-detail-prob">{t.current.fraud_probability != null ? `${(t.current.fraud_probability * 100).toFixed(0)}%` : "Model not trained"}</div><div className="text-[10px] text-slate-400">Learned from labels; not a validated probability</div></div>
        </div>
        <div className="text-[10px] text-slate-500 mt-2">Status <b data-testid="fraud-detail-status">{t.status}</b> · decided in {s.total_latency_ms ?? s.latency_ms} ms · baseline {t.features.baseline}{t.current_version !== s.version ? ` · re-scored under ${t.current_version}: ${t.current.band} ${t.current.score}` : ""}</div>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="panel p-4" data-testid="fraud-fired-rules">
          <div className="eyebrow mb-2">Why - rules that fired</div>
          {s.hard_controls.map((h) => <div key={h.control} className="text-xs bg-red-50 text-red-800 rounded-lg p-2 mb-2"><b>{h.control}:</b> {h.detail}. {h.action}</div>)}
          {s.fired.map((f) => (
            <div key={f.rule_id} className="flex justify-between gap-3 py-1.5 border-t border-slate-100 first:border-0 text-xs">
              <div><div className="font-medium text-slate-800">{f.name}</div><div className="text-[11px] text-slate-500">{f.category} · {f.evidence}</div></div>
              <span className="num font-semibold text-slate-900">+{f.points}</span>
            </div>))}
          {!s.fired.length && !s.hard_controls.length && <div className="text-xs text-slate-500">No rules fired - normal for this customer.</div>}
          {t.shadow.map((x) => <div key={x.version} className="text-[11px] text-sky-700 mt-2">Shadow {x.version}: would score {x.score} ({x.band})</div>)}
        </div>
        <Signals f={t.features} />
      </div>
      <Explain t={t} />
      <ActionBox t={t} onData={upd} />
    </div>
  );
}

function Signals({ f }) {
  const rows = Object.entries(f).filter(([k]) => !["baseline", "baseline_known", "sanctions_detail"].includes(k));
  return (
    <div className="panel p-4" data-testid="fraud-signals">
      <div className="eyebrow mb-2">Signals (normalized features)</div>
      <div className="grid grid-cols-2 gap-x-4 text-[11px]">
        {rows.map(([k, v]) => <div key={k} className="flex justify-between border-t border-slate-100 py-1"><span className="text-slate-500">{k}</span><span className={`num ${v === true ? "text-red-700 font-semibold" : "text-slate-800"}`}>{v === null ? "n/a" : String(v)}</span></div>)}
      </div>
    </div>
  );
}

function Explain({ t }) {
  const [text, setText] = useState(t.ai_explanation?.text);
  const [busy, setBusy] = useState(false);
  const run = safe(async () => { setBusy(true); try { setText((await api.post(`/fraud/transactions/${t.id}/explain`)).data.text); } finally { setBusy(false); } });
  return (
    <div className="panel p-4" data-testid="fraud-explain">
      <div className="flex justify-between items-center"><div className="eyebrow">AI investigation agent</div>
        <button data-testid="fraud-explain-btn" onClick={run} disabled={busy} className={ghostCls}><Bot className="w-3.5 h-3.5" />{busy ? "Investigating…" : text ? "Re-run" : "Explain this alert"}</button></div>
      {text ? <div className="text-xs text-slate-700 whitespace-pre-wrap mt-2" data-testid="fraud-explain-text">{text.replace(/\*\*/g, "")}</div> : <div className="text-[11px] text-slate-500 mt-1">Runs separately from the payment decision, so it never slows down authorization.</div>}
    </div>
  );
}

function ActionBox({ t, onData }) {
  const nav = useNavigate();
  const [a, setA] = useState(t.scored.band === "red" ? "Hold" : t.scored.band === "yellow" ? "Request verification" : "Allow / release");
  const [why, setWhy] = useState("");
  const act = safe(async () => { onData((await api.post(`/fraud/transactions/${t.id}/action`, { action: a, rationale: why })).data); setWhy(""); toast.success(`${a} recorded`); });
  const lab = (outcome) => safe(async () => {
    const r = await api.post(`/fraud/transactions/${t.id}/label`, { outcome });
    onData(r.data);
    toast.success(r.data.auto_tune?.length ? `Labeled. AI re-tuned ${r.data.auto_tune.length} rule weight(s) - new version active` : `Labeled: ${outcome}`);
  })();
  const kase = safe(async () => { const r = await api.post("/aml/cases", { kind: "fraud", item_ids: [t.id], typology: "Payment fraud" }); toast.success(`Case ${r.data.id} opened`); nav(`/aml?tab=cases&case=${r.data.id}`); });
  return (
    <div className="panel p-4 space-y-3" data-testid="fraud-action-box">
      <div className="eyebrow">Hold / approval controls</div>
      <div className="flex flex-wrap gap-2">
        <select data-testid="fraud-action-select" value={a} onChange={(e) => setA(e.target.value)} className={inputCls}>{ACTIONS.map((x) => <option key={x}>{x}</option>)}</select>
        <input data-testid="fraud-action-rationale" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Rationale (required, 10+ characters)" className={`${inputCls} flex-1 min-w-[220px]`} />
        <button data-testid="fraud-action-submit" disabled={why.trim().length < 10} onClick={act} className={btnCls}>Record</button>
        <button data-testid="fraud-open-case" onClick={kase} className={ghostCls}><FolderPlus className="w-3.5 h-3.5" />Open case</button>
      </div>
      <div className="text-[10px] text-slate-500">Releasing a red or held payment needs an approver who did not place the hold. Settled payments cannot be held - open an investigation instead.</div>
      <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
        <Tag className="w-3.5 h-3.5 text-slate-400" /><span className="text-xs text-slate-600">Verified outcome {t.label ? <b data-testid="fraud-label">{t.label.outcome} ({t.label.by})</b> : "- teaches the AI"}</span>
        <button data-testid="fraud-label-fraud" onClick={() => lab("Confirmed fraud")} className={`${ghostCls} text-red-700`}>Confirmed fraud</button>
        <button data-testid="fraud-label-notfraud" onClick={() => lab("Not fraud")} className={`${ghostCls} text-emerald-700`}>Not fraud</button>
      </div>
      {t.actions.length > 0 && <div className="border-t border-slate-100 pt-2 space-y-1" data-testid="fraud-action-history">{t.actions.map((x, n) => <div key={n} className="text-[11px] text-slate-600"><b>{x.action}</b> by {x.by} · {new Date(x.at).toLocaleString()} - {x.rationale}{x.override ? " (override)" : ""}</div>)}</div>}
    </div>
  );
}
